import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { isAuthenticatedSelector, actions } from "../redux/userSlice";
import { Link, NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navbar, Nav, NavItem, Button, Collapse, NavbarToggler } from "reactstrap";

// Import icons
import { FaBars, FaTimes, FaHome, FaStore, FaSignInAlt, FaUserPlus, FaSignOutAlt, FaBook, FaRobot, FaChild, FaClipboardList, FaFileAlt, FaCheckCircle, FaChartBar, FaUsers, FaUserFriends, FaVideo, FaShareAlt, FaProjectDiagram } from "react-icons/fa";

interface Language {
  nativeName: string;
}
interface Langs {
  [key: string]: Language;
}
const langs: Langs = {
  en: { nativeName: "EN" },
  bg: { nativeName: "BG" },
};

function Navigation() {
  const { avatar, firstName, lastName, isAutoCreated } =
    useAppSelector(isAuthenticatedSelector);
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const access = localStorage.getItem("access");
  const refresh = localStorage.getItem("refresh");
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (location.pathname === "/login") {
      dispatch(actions.logout());
    }
  }, [location, dispatch]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleNavbar = () => setCollapsed(!collapsed);

  return (
    <div>
      {/* TOP NAVBAR */}
      <Navbar color="dark" dark expand="lg" className="px-3 d-flex align-items-center">
        {/* Sidebar toggle */}
        {access && refresh && <Button
          color="link"
          onClick={toggleSidebar}
          className="p-0 me-3"
          style={{
            fontSize: "1.8rem",
            minWidth: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "white",
          }}
        >
          <FaBars />
        </Button>
        }

        {/* Logo / Home */}
        <Link to="/" className="navbar-brand me-4 text-white d-flex align-items-center">
          <FaHome className="me-2" /> {t("navigation.home")}
        </Link>

        {/* Navbar toggler for mobile */}
        <NavbarToggler onClick={toggleNavbar} className="me-2" />

        {/* Collapse horizontal menu */}
        <Collapse isOpen={!collapsed} navbar className="flex-grow-1">
          <div className="d-flex flex-column flex-lg-row justify-content-between w-100 align-items-start align-items-lg-center">
            {/* Left menu */}
            <Nav navbar className="d-flex align-items-center">
              <NavItem className="me-3">
                <RouterNavLink to="/market-public" className="nav-link text-white d-flex align-items-center">
                  <FaStore className="me-2" /> {t("navigation.market")}
                </RouterNavLink>
              </NavItem>
            </Nav>

            {/* Right menu */}
            <Nav navbar className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center mt-3 mt-lg-0">
              {!access && !refresh && (
                <>
                  <NavItem className="me-2">
                    <RouterNavLink to="/login" className="nav-link text-white d-flex align-items-center">
                      <FaSignInAlt className="me-2" /> {t("navigation.login")}
                    </RouterNavLink>
                  </NavItem>
                  <NavItem className="me-2">
                    <RouterNavLink to="/signup" className="nav-link text-white d-flex align-items-center">
                      <FaUserPlus className="me-2" /> {t("navigation.signup")}
                    </RouterNavLink>
                  </NavItem>
                </>
              )}

              {access && refresh && (
                <>
                  <NavItem className="me-2 d-flex align-items-center">
                    <RouterNavLink to="/profile" className="nav-link d-flex align-items-center text-white">
                      <img
                        src={avatar}
                        alt="profile"
                        style={{
                          height: 35,
                          width: 35,
                          borderRadius: "50%",
                          marginRight: 8,
                          objectFit: "cover",
                        }}
                      />
                      {/* <FaUser className="me-2" /> */}
                      {firstName} {lastName}
                    </RouterNavLink>
                  </NavItem>
                  <NavItem className="me-2">
                    <RouterNavLink to="/logout" className="nav-link text-white d-flex align-items-center">
                      <FaSignOutAlt className="me-2" /> {t("navigation.logout")}
                    </RouterNavLink>
                  </NavItem>
                </>
              )}

              {/* Language buttons */}
              {Object.keys(langs).map((lng) => (
                <Button
                  key={lng}
                  className="languages-buttons ms-2"
                  style={{
                    fontWeight: i18n.resolvedLanguage === lng ? "bold" : "normal",
                    padding: "0.2rem 0.6rem",
                    minWidth: "40px",
                  }}
                  onClick={() => i18n.changeLanguage(lng)}
                >
                  {langs[lng].nativeName}
                </Button>
              ))}
            </Nav>
          </div>
        </Collapse>
      </Navbar>

      {/* SIDE MENU */}
      {access && refresh && <div
        className={`sidebar bg-dark ${sidebarOpen ? "open" : ""}`}
        style={{
          width: sidebarOpen ? 250 : 0,
          transition: "0.3s",
          overflowX: "hidden",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 1050,
          paddingTop: "60px",
          color: "white",
        }}
      >
        {/* Close button fixed at top-right of sidebar */}
        {sidebarOpen && (
          <Button
            color="link"
            onClick={toggleSidebar}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              fontSize: "1.8rem",
              color: "white",
            }}
          >
            <FaTimes />
          </Button>
        )}

        <Nav vertical className="px-3">
        
          {access && refresh && (
            <>
              <h6 className="text-white mt-3">{t("navigation.flashCardsCategories")}</h6>
              <NavItem>
                <RouterNavLink to="/my-categories" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaBook className="me-2" /> {t("navigation.myCategories")}
                </RouterNavLink>
              </NavItem>
              {!isAutoCreated && (
                <NavItem>
                  <RouterNavLink to="/ai-cards" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                    <FaRobot className="me-2" /> {t("navigation.aiCategories")}
                  </RouterNavLink>
                </NavItem>
              )}
              <NavItem>
                <RouterNavLink to="/kids" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaChild className="me-2" /> {t("navigation.kidsCategories")}
                </RouterNavLink>
              </NavItem>


              <h6 className="text-white mt-3">{t("navigation.assignFromMe")}</h6>
              <NavItem>
                <RouterNavLink to="/school-tests" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaClipboardList className="me-2" /> {t("navigation.myTests")}
                </RouterNavLink>
              </NavItem>
              <NavItem>
                <RouterNavLink to="/school-exams" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaFileAlt className="me-2" /> {t("navigation.myExams")}
                </RouterNavLink>
              </NavItem>
              {!isAutoCreated && (
                <NavItem>
                  <RouterNavLink to="/reports" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                    <FaChartBar className="me-2" /> {t("navigation.reports")}
                  </RouterNavLink>
                </NavItem>
              )}
              {!isAutoCreated && (
                <NavItem>
                  <RouterNavLink to="/learning-paths" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                    <FaProjectDiagram className="me-2" /> {t("navigation.learningPaths")}
                  </RouterNavLink>
                </NavItem>
              )}


              <h6 className="text-white mt-3">{t("navigation.assignToMe")}</h6>
              <NavItem>
                <RouterNavLink to="/send-to-me-categories" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaShareAlt className="me-2" /> {t("navigation.sendToMe")}
                </RouterNavLink>
              </NavItem>
              {!isAutoCreated && (
                <NavItem>
                <RouterNavLink to="/students-school-exams" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaCheckCircle className="me-2" /> {t("navigation.examsAssignToMe")}
                </RouterNavLink>
                </NavItem>
              )}
              {!isAutoCreated && (
                <NavItem>
                  <RouterNavLink to="/my-learning-paths" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                    <FaProjectDiagram className="me-2" /> {t("navigation.learningPaths")}
                  </RouterNavLink>
                </NavItem>
              )}

              <h6 className="text-white mt-3">{t("navigation.social")}</h6>
              <NavItem>
                <RouterNavLink to="/friends" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaUserFriends className="me-2" /> {t("navigation.friends")}
                </RouterNavLink>
              </NavItem>
              {!isAutoCreated && (
                <NavItem>
                  <RouterNavLink to="/user-groups" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                    <FaUsers className="me-2" /> {t("navigation.myUserGroups")}
                  </RouterNavLink>
                </NavItem>
              )}

              <h6 className="text-white mt-3">{t("navigation.other")}</h6>
              <NavItem>
                <RouterNavLink to="/demo-videos" className="nav-link text-white d-flex align-items-center" onClick={toggleSidebar}>
                  <FaVideo className="me-2" /> {t("navigation.demoVideos")}
                </RouterNavLink>
              </NavItem>
              
              
            </>
          )}
        </Nav>
      </div>}
    </div>
  );
}

export default Navigation;




// import { Button, Collapse, Nav, Navbar, NavbarToggler, NavItem } from "reactstrap"
// import { isAuthenticatedSelector } from "../redux/userSlice";
// import { useAppDispatch, useAppSelector } from '../redux/store';
// import { useEffect, useState } from "react";
// import { actions } from '../redux/userSlice'
// import { Link, useLocation } from 'react-router-dom';
// import { NavLink as RouterNavLink } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';

// interface Language {
//   nativeName: string;
// }

// interface Langs {
//   [key: string]: Language;
// }


// const langs: Langs = {
//   en: { nativeName: 'EN' },
//   bg: { nativeName: 'BG' }
// };

// function Navigation() {
//   const {
//     avatar,
//     firstName,
//     lastName,
//     isAutoCreated
//   } = useAppSelector(isAuthenticatedSelector)
//   const [collapsed, setCollapsed] = useState(true);
//   const { t, i18n } = useTranslation();
//   const toggleNavbar = () => setCollapsed(!collapsed);

//   const access = localStorage.getItem('access');
//   const refresh = localStorage.getItem('refresh');
//   const location = useLocation();
//   const dispatch = useAppDispatch();

//   useEffect(() => {
//     if (location.pathname === "/login") {
//       dispatch(actions.logout())
//     }
//   }, [location, dispatch])

//   return (
//     <div className="navigation-menu">
//       <Navbar color="dark" dark className="navbar-expand-lg">
//       <Link to="/" className="navbar-brand me-auto">
//         {t('navigation.home')}
//       </Link>
//         <NavbarToggler onClick={toggleNavbar} className="me-2" />
//         <Collapse isOpen={!collapsed} navbar>
//           <Nav navbar className="navbar-expand-lg">
//             <div className="nav-market-public">
//               {!access && !refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/market-public" className="nav-link">
//                 {t('navigation.market')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-market-public">
//               {!access && !refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/demo-videos" className="nav-link">
//                 {t('navigation.demoVideos')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-market-public">
//               {!access && !refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/teachers" className="nav-link">
//                 {t('navigation.teachers')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
            
//             <div className="nav-my-categories">
//               {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/my-categories" className="nav-link">
//                   {t('navigation.myCategories')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-my-categories">
//               {access && refresh && !isAutoCreated && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/ai-cards" className="nav-link">
//                   {t('navigation.aiCategories')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-market" onClick={toggleNavbar}>
//               {access && refresh && <NavItem>
//                 <RouterNavLink to="/market" className="nav-link">
//                   {t('navigation.market')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-my-categories">
//               {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/kids" className="nav-link">
//                 {t('navigation.kidsCategories')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-send-to-me-categories">
//               {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/send-to-me-categories" className="nav-link">
//                   {t('navigation.sendToMe')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-user-groups">
//               {access && refresh && !isAutoCreated && <NavItem>
//                 <RouterNavLink to="/user-groups" className="nav-link">
//                   {t('navigation.myUserGroups')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-user-groups">
//               {access && refresh && !isAutoCreated && <NavItem>
//                 <RouterNavLink to="/reports" className="nav-link">
//                   {t('navigation.reports')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-user-groups">
//               {access && refresh && !isAutoCreated && <NavItem>
//                 <RouterNavLink to="/my-learning-paths" className="nav-link">
//                   {t('navigation.learningPaths')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-market-public">
//             {access && refresh && !isAutoCreated && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/friends" className="nav-link">
//                 {t('navigation.friends')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             {/* {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/subscriptions" className="nav-link">
//                 {t('navigation.subscription')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
            
//             <div className="nav-user-groups">
//               {access && refresh && <NavItem>
//                 <RouterNavLink to="/demo-videos-private" className="nav-link">
//                   {t('navigation.demoVideos')}
//                 </RouterNavLink>
//               </NavItem>} */}
//           </Nav>
//           <Nav navbar className="navbar-expand-lg ms-auto me-0">
//           <div className="nav-login">
//               {!access && !refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/login" className="nav-link">
//                   {t('navigation.login')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="nav-signup">
//               {!access && !refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink
//                   to="/signup"
//                   className="nav-link"
//                 >
//                   {t('navigation.signup')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="d-flex align-items-center">
//               {access && refresh &&
//                 <RouterNavLink to="/profile" className="nav-link" onClick={toggleNavbar}>
//                   <img
//                     alt="logo"
//                     //className="avatar-image"
//                     src={avatar}
//                     style={{
//                       height: 40,
//                       width: 40,
//                       borderRadius: 50,
//                       objectFit: "cover"
//                     }}
//                   />
//                 </RouterNavLink>}
//               {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/profile" className="nav-link">
//                   {firstName} {lastName}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="d-flex align-items-center">
//               {access && refresh && <NavItem onClick={toggleNavbar}>
//                 <RouterNavLink to="/logout" className="nav-link">
//                   {t('navigation.logout')}
//                 </RouterNavLink>
//               </NavItem>}
//             </div>
//             <div className="d-flex align-items-center">
//               {Object.keys(langs).map((lng) => (
//                 <NavItem key={lng} onClick={toggleNavbar}>
//                 <Button
//                   className="languages-buttons"
//                   key={lng} 
//                   style={{ fontWeight: i18n.resolvedLanguage === lng ? 'bold' : 'normal', 
//                   marginRight: '10px',
//                   padding: '0.175rem 0.75rem',
//                   }} 
//                   type="submit" 
//                   onClick={() => i18n.changeLanguage(lng)}
//                   >
//                   {langs[lng].nativeName}
//                 </Button>
//                 </NavItem>
//               ))}
//             </div>
//           </Nav>
//         </Collapse>
//       </Navbar>
//     </div>
//   )
// }

// export default Navigation